import React, { useState, useMemo, useCallback, useContext } from "react";
import {
  TRAVEL_WALLET_DRAWER_TITLES,
  getTravelOfferCardContent,
  isOfferHSPTiered,
  TravelOfferCardMinimal,
  TravelWalletDetailsModal,
  TravelWalletDrawer,
  useDeviceTypes,
  TRAVEL_WALLET_MORE_INFO_TEXT,
  TRAVEL_CREDIT_TOOLTIP_TEXT,
  TRAVEL_OFFERS_TOOLTIP_TEXT,
  getRestrictedUserTCTexts,
  filterOutSpendAndGetOffers,
} from "halifax";
import { RouteComponentProps } from "react-router";
import {
  CorpSessionInfo,
  ENGAGED_OFFER_CTA,
  Funnel,
  TravelWalletOffer,
  VIEW_CREDIT_HISTORY,
  VIEW_TRAVEL_WALLET,
  MediaEventType,
  MediaPlacement,
} from "redmond";

import { getConfigTenant, isCorpTenant, getTrackingEntryPoint } from "@capone/common";
import { TravelWalletDrawerConnectorProps } from "./container";
import * as constants from "./constants";
import { TravelCreditBalanceCard } from "../TravelCreditBalanceCard";
import {
  getExperimentVariant,
  CREDIT_OFFER_STACKING_V1,
  AVAILABLE,
  useExperiments,
  TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  ANNUAL_TRAVEL_CREDITS,
  getExperimentVariantCustomVariants,
  CONTROL,
  TRAVEL_SALE,
  TRAVEL_SALE_LEAD_UP,
  TRAVEL_SALE_VARIANTS,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";
import { ClientContext } from "../../../../App";
import { PATH_HOTEL, PATH_TRAVEL_SALE } from "../../../../utils/paths";
import "./styles.scss";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { trackMediaEvent } from "../../../../../../../cap1-application/b2b-base/src/api/v1/trackHTSMedia";
import clsx from "clsx";

export interface IVacationRentalTravelWalletDrawerProps
  extends TravelWalletDrawerConnectorProps,
    RouteComponentProps {}

export const VacationRentalTravelWalletDrawer = ({
  allOffers,
  activeOffers,
  rewardsAccounts,
  travelCredit,
  creditBreakdown,
  history,
}: IVacationRentalTravelWalletDrawerProps) => {
  const [selectedOffer, setSelectedOffer] = useState<
    { offer: TravelWalletOffer; index?: number } | undefined
  >(undefined);

  const { sessionInfo } = useContext(ClientContext);

  const firstName = sessionInfo?.userInfo?.firstName || "";

  const isNonFinancialUser =
    (sessionInfo as CorpSessionInfo)?.corporateInfo?.cap1Role === "NFU";

  const { matchesMobile } = useDeviceTypes();
  const expState = useExperiments();

  const travelSalesEventVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    TRAVEL_SALE,
    TRAVEL_SALE_VARIANTS,
  );

  const travelOffers = useMemo(
    () =>
      filterOutSpendAndGetOffers(
        travelSalesEventVariant === TRAVEL_SALE_LEAD_UP
          ? activeOffers
          : allOffers,
      ),
    [travelSalesEventVariant, activeOffers, allOffers],
  );

  const totalWalletCount = useMemo(
    () => (travelCredit ? travelOffers.length + 1 : travelOffers.length),
    [travelOffers, travelCredit],
  );

  const creditAndOfferStackingExperimentV1 = getExperimentVariant(
    expState.experiments,
    CREDIT_OFFER_STACKING_V1,
  );
  const isCreditAndOfferStackingExperimentV1 = React.useMemo(
    () => creditAndOfferStackingExperimentV1 === AVAILABLE,
    [creditAndOfferStackingExperimentV1],
  );

  const travelCreditHistoryExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  );
  const isTravelCreditHistoryExperiment = useMemo(
    () => travelCreditHistoryExperiment === AVAILABLE,
    [travelCreditHistoryExperiment],
  );

  const isAnnualTravelCreditsExperiment =
    getExperimentVariant(expState.experiments, ANNUAL_TRAVEL_CREDITS) ===
    AVAILABLE;

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const getOfferCTAs = useCallback(
    (
      offer: TravelWalletOffer,
      includeViewOfferDetailsCTA?: boolean,
      offerRowIndex?: number,
    ) => {
      const offerIndexToUse =
        offerRowIndex !== undefined ? offerRowIndex + 1 : undefined;
      const trackOfferEngagementEvent = () => {
        trackEvent({
          eventName: ENGAGED_OFFER_CTA,
          properties: {
            location: "premium_stays_wallet_drawer",
            entry_type:
              matchesMobile && !includeViewOfferDetailsCTA ? "modal" : "card",
            index_row_id: offerIndexToUse,
            funnel: offer.funnels.join(","),
          },
          encryptedProperties: [
            offer.trackingPropertiesV2?.encryptedProperties ?? "",
          ],
        });

        trackMediaEvent({
          eventType: MediaEventType.Click,
          trackingProperties: offer?.trackingPropertiesV2,
          placement: MediaPlacement.PremierCollectionTravelWalletDrawer,
        });
      };

      const { funnels } = offer;
      // Sort funnels to move Home/Homes to the end (has longest text so it should take full width if needed)
      const sortedFunnels = [...funnels].sort((a, b) => {
        const isAHome = a === Funnel.Home || a === Funnel.Homes;
        const isBHome = b === Funnel.Home || b === Funnel.Homes;
        if (isAHome && !isBHome) return 1;
        if (!isAHome && isBHome) return -1;
        return 0;
      });

      const hasHomeFunnel = funnels.some(
        (f) => f === Funnel.Home || f === Funnel.Homes,
      );
      const shouldMakeSecondToLastFullWidth =
        sortedFunnels.length % 2 === 0 &&
        hasHomeFunnel &&
        sortedFunnels.length >= 2;

      const funnelButton = sortedFunnels.map((funnel, index) => {
        const isSecondToLast = index === sortedFunnels.length - 2;
        const isHome = funnel === Funnel.Home || funnel === Funnel.Homes;
        const shouldBeFullWidth =
          isHome || (shouldMakeSecondToLastFullWidth && isSecondToLast);

        switch (funnel) {
          case Funnel.Air:
            return {
              text: "Search flights",
              onClick: () => {
                history.push("/flights/");
                trackOfferEngagementEvent();
              },
              fullWidth: shouldBeFullWidth,
            };
          case Funnel.Lodging:
            if (
              isCorpTenant(getConfigTenant()) &&
              offer.descriptions &&
              offer.descriptions[0].toLowerCase().includes("hotels + flights")
            ) {
              return {
                text: "Search hotels + flights",
                onClick: () => {
                  history.push("/hotels-flights/");
                  trackOfferEngagementEvent();
                },
                fullWidth: shouldBeFullWidth,
              };
            }
            if (
              offer.descriptions &&
              offer.descriptions[0].includes("Premier Collection")
            ) {
              return {
                text: "Search premium stays",
                onClick: () => {
                  history.push("/premium-stays/");
                  trackOfferEngagementEvent();
                },
                fullWidth: shouldBeFullWidth,
              };
            }
            return {
              text: "Search hotels",
              onClick: () => {
                history.push(PATH_HOTEL);
                trackOfferEngagementEvent();
              },
              fullWidth: shouldBeFullWidth,
            };
          case Funnel.Homes:
          case Funnel.Home:
            return {
              text: "Search vacation rentals",
              onClick: () => {
                history.push("/premium-stays/vacation-rentals/");
                trackOfferEngagementEvent();
              },
              fullWidth: shouldBeFullWidth,
            };
          case Funnel.Ground:
            return {
              text: "Search cars",
              onClick: () => {
                history.push("/cars/");
                trackOfferEngagementEvent();
              },
              fullWidth: shouldBeFullWidth,
            };
          case Funnel.Experiences:
          case Funnel.Activity:
            return {
              text: "Search activities",
              onClick: () => {
                history.push("/activities/");
                trackOfferEngagementEvent();
              },
              fullWidth: shouldBeFullWidth,
            };
          case Funnel.Package:
            return {
              text: "Search packages",
              onClick: () => {
                history.push("/packages/");
                trackOfferEngagementEvent();
              },
              fullWidth: shouldBeFullWidth,
            };
        }
      });

      return [
        ...funnelButton,
        ...(includeViewOfferDetailsCTA
          ? [
              {
                text: "View offer details",
                onClick: () => {
                  setSelectedOffer({ offer, index: offerRowIndex });
                  trackOfferEngagementEvent();
                },
              },
            ]
          : []),
      ];
    },
    [history],
  );

  const renderTravelOffersCards = () =>
    travelOffers.length >= 1 ? (
      <>
        {travelOffers.map((offer) => {
          const offerContent = getTravelOfferCardContent(
            offer,
            isCreditAndOfferStackingExperimentV1,
          );

          const offerLocation =
            offer.descriptions.length >= 3
              ? offer?.descriptions[2]
              : offer.descriptions[0];

          return (
            <TravelOfferCardMinimal
              key={offer.id}
              title={
                isOfferHSPTiered(offer.descriptions)
                  ? `<strong>${offer.shopPageBanner}</strong>` || offer.title
                  : offer.title
              }
              expirationString={offerContent.expirationDetail}
              validityString={offerContent.applicabilityDetail}
              ctas={getOfferCTAs(offer, true)}
              offerFunnels={offer.funnels}
              offerLocation={offerLocation}
              imageId={offer.imageId}
              imageFolderUrl={offer.imageFolderUrl}
            />
          );
        })}
      </>
    ) : (
      <TravelOfferCardMinimal
        title={constants.NO_OFFERS_AVAIL_TITLE}
        body={constants.NO_OFFER_AVAIL_BODY}
      />
    );
  return (
    <>
      {selectedOffer && (
        <TravelWalletDetailsModal
          openModal={!!selectedOffer}
          setOpenModal={() => setSelectedOffer(undefined)}
          offerCardProps={{
            offerContent: getTravelOfferCardContent(
              selectedOffer.offer,
              isCreditAndOfferStackingExperimentV1,
            ),
            ctas: matchesMobile
              ? getOfferCTAs(selectedOffer.offer, false, selectedOffer.index)
              : undefined,
            buttonClassName: "b2b",
            titleLabelText: isOfferHSPTiered(selectedOffer.offer.descriptions)
              ? constants.HSP_OFFER_TITLE_LABEL
              : undefined,
            className: clsx({ xmod: isXmodRedesign }),
          }}
          termsType={matchesMobile ? "accordion" : "link"}
          isMobile={matchesMobile}
          offerFunnels={selectedOffer.offer.funnels}
          offerLocation={selectedOffer.offer.descriptions[2]}
          isTravelSale={travelSalesEventVariant !== CONTROL}
          imageId={selectedOffer.offer.imageId}
          imageFolderUrl={selectedOffer.offer.imageFolderUrl}
          onViewCreditHistory={() =>
            trackEvent({
              eventName: VIEW_CREDIT_HISTORY,
              properties: {},
            })
          }
        />
      )}
      <TravelWalletDrawer
        creditBalanceCard={
          <TravelCreditBalanceCard
            isTravelCreditHistoryExperiment={isTravelCreditHistoryExperiment}
            isAnnualTravelCreditsExperiment={isAnnualTravelCreditsExperiment}
            isMobile={matchesMobile}
            onViewCreditHistory={() =>
              trackEvent({
                eventName: VIEW_CREDIT_HISTORY,
                properties: {},
              })
            }
          />
        }
        offersCards={renderTravelOffersCards()}
        travelCreditTooltipText={TRAVEL_CREDIT_TOOLTIP_TEXT(
          isCreditAndOfferStackingExperimentV1,
          isNonFinancialUser,
        )}
        travelOfferTooltipText={TRAVEL_OFFERS_TOOLTIP_TEXT(
          isCreditAndOfferStackingExperimentV1,
        )}
        moreInfoText={TRAVEL_WALLET_MORE_INFO_TEXT}
        drawerTitles={TRAVEL_WALLET_DRAWER_TITLES(travelOffers)}
        isMobile={matchesMobile}
        offersCount={totalWalletCount}
        rewardsAccounts={rewardsAccounts}
        firstName={firstName}
        restrictedUserTCTexts={getRestrictedUserTCTexts(
          rewardsAccounts,
          creditBreakdown,
        )}
        creditBreakdown={creditBreakdown}
        travelSalesEventBannerPlacement={
          travelSalesEventVariant !== CONTROL
            ? travelSalesEventVariant
            : undefined
        }
        travelSalesBannerProps={
          travelSalesEventVariant !== CONTROL
            ? {
                subtitle:
                  travelSalesEventVariant === TRAVEL_SALE_LEAD_UP
                    ? constants.TRAVEL_SALES_EVENT_LEADUP_SUBTITLE
                    : constants.TRAVEL_SALES_EVENT_ACTIVE_SUBTITLE,
                ctaText:
                  travelSalesEventVariant === TRAVEL_SALE_LEAD_UP
                    ? constants.TRAVEL_SALES_EVENT_LEADUP_CTA
                    : constants.TRAVEL_SALES_EVENT_ACTIVE_CTA,
                ctaOnClick: () => {
                  matchesMobile
                    ? history.push(PATH_TRAVEL_SALE)
                    : window.open(PATH_TRAVEL_SALE, "_blank");
                },
              }
            : undefined
        }
        includeOffers
        onOpenDrawer={() =>
          trackEvent({
            eventName: VIEW_TRAVEL_WALLET,
            properties: {
              entry_point: getTrackingEntryPoint(window.location.pathname),
            },
          })
        }
      />
    </>
  );
};
